import React, { memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { DashboardStats } from '@/polymet/types/supplier';
import { TrendingUp, Package, MessageSquare, ShoppingCart, IndianRupee } from 'lucide-react';

interface StatsOverviewProps {
  stats: DashboardStats;
  loading?: boolean;
  className?: string;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  loading?: boolean;
  ariaLabel?: string;
}

const StatCard = memo<StatCardProps>(({ title, value, icon, loading = false, ariaLabel }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground" id={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            {title}
          </p>
          {loading ? (
            <div className="h-8 w-20 bg-muted animate-pulse rounded" />
          ) : (
            <p 
              className="text-2xl font-bold"
              aria-labelledby={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
              aria-label={ariaLabel || `${title}: ${value}`}
            >
              {value}
            </p>
          )}
        </div>
        <div className="text-primary opacity-70">
          {icon}
        </div>
      </div>
    </CardContent>
  </Card>
));

StatCard.displayName = 'StatCard';

const StatsOverview = memo<StatsOverviewProps>(({ stats, loading = false, className = '' }) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const statsConfig = [
    {
      title: 'Total Products',
      value: stats.products,
      icon: <Package className="h-6 w-6" />,
      ariaLabel: `${stats.products} products listed`
    },
    {
      title: 'New Inquiries',
      value: stats.inquiries,
      icon: <MessageSquare className="h-6 w-6" />,
      ariaLabel: `${stats.inquiries} new inquiries received`
    },
    {
      title: 'Active Orders',
      value: stats.orders,
      icon: <ShoppingCart className="h-6 w-6" />,
      ariaLabel: `${stats.orders} active orders`
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(stats.revenue),
      icon: <IndianRupee className="h-6 w-6" />,
      ariaLabel: `Total revenue: ${formatCurrency(stats.revenue)}`
    }
  ];

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
      {statsConfig.map((stat) => (
        <StatCard
          key={stat.title}
          title={stat.title}
          value={stat.value}
          icon={stat.icon}
          loading={loading}
          ariaLabel={stat.ariaLabel}
        />
      ))}
    </div>
  );
});

StatsOverview.displayName = 'StatsOverview';

export default StatsOverview; 